import { CmsImageData } from './cmsTypes';
import { h } from 'preact';

export enum Environment {
  TEST = 'test',
  PREPROD = 'preprod',
  PROD = 'prod',
}

export enum WidgetTab {
  FLIGHT = 'flight',
  AWARD = 'award',
  AM = 'aurinkomatkat',
}

export enum ContentType {
  FRONT_PAGE = 'front_page',
  SINGLE_DESTINATION = 'single_destination',
}

export enum TravelType {
  ROUND_TRIP = 'return',
  ONE_WAY = 'oneway',
  MULTI_CITY = 'multiCity',
}

export enum TravelClass {
  MIXED = 'mixed',
  ECONOMY = 'economy',
  PREMIUM_ECONOMY = 'ecopremium',
  BUSINESS = 'business',
}

export enum PaxType {
  ADULT = 'adult',
  CHILD = 'child',
  C15 = 'c15',
  INFANT = 'infant',
}

export enum LocationCodeType {
  CITY = 'city',
  AIRPORT = 'airport',
}

export enum ClientType {
  LAPTOP = 'laptop',
  DESKTOP = 'desktop',
  MOBILE_TABLET = 'mobile/tablet',
}

export interface PaxAmount {
  adults: number;
  children: number;
  c15s: number;
  infants: number;
}

export type PaxAmountKey = keyof PaxAmount;

export interface PaxSelectionOption {
  type: PaxAmountKey;
  label: string;
  amount: number;
  canAdd: boolean;
  canRemove: boolean;
}

export type AmPaxType = 'adults' | 'children';
export type AmPaxAmount = {
  [key in AmPaxType]: number;
} & {
  childrenAges: Array<number | null>;
  roomIndex: number;
};

export type AmRooms = AmPaxAmount[];

export interface AmPaxAmountTotals {
  total: {
    [key in AmPaxType]: number;
  };
  remaining: {
    [key in AmPaxType]: number;
  };
}

export enum LocationType {
  ORIGIN = 'origin',
  DESTINATION = 'destination',
}

export interface Location {
  title: string;
  country: string;
  countryCode: string;
  locationCode: string;
  cityName?: string;
  destinationUrl?: string;
  /** true, if the location is the nearest airport determined by the user's geolocation */
  isFromUserLocation?: boolean;
  distanceKm?: number;
  /**
   * The city code if not equal to locationCode.
   * E.g., locationCityCode=BER for locationCode=TXL, but undefined for HEL
   */
  locationCityCode?: string;
  AJBPartner: boolean;
  SJBPartner: boolean;
  airAward: boolean;
  airPartner: boolean;
  airAlliance: boolean;
  type: 'city' | 'airport';
  latitude: number;
  longitude: number;
  /** Picture matched from cms destination page to location */
  picture?: CmsImageData | null;
  online?: boolean;
  continentCode?: string;
}

export enum AmDurationType {
  LIST = 'list',
  DAYS = 'days',
}

export enum SearchType {
  BOOKING_FLOW = 'booking-flow',
  AWARD = 'award',
}

export type AmDurationItem = {
  label: string;
  code: string;
};

export type AmDateRange = {
  from: string; // ISO date
  to: string; // ISO date
};

export type AmHolidayType = {
  label: string;
  type: AmDurationType;
  items?: AmDurationItem[]; // items are optional if type is DAYS
  maxDays?: number; // maxDays and minDays are optional if type is LIST
  minDays?: number;
};

export type AmHolidayTypes = {
  [k: string]: AmHolidayType;
};

export interface AmLocation {
  countryCode: string;
  destinationCode: string; // Airport code
  country: string;
  city: string;
  holidayType: string;
  searchable: AmDateRange[];
}

export type AmDestinations = {
  holidayTypes: AmHolidayTypes;
  destinations: AmLocation[];
};

export interface Locations {
  origin?: Location;
  destination?: Location;
}

export interface AmLocations {
  origin?: Location;
  destination?: AmLocation;
  holidayType?: AmHolidayType;
}

export interface LocationSuggestions {
  departures: Location[];
  destinations: Location[];
}

export interface TrendingDestinations {
  locations: Location[];
}

export type SuggestedLocationParams = {
  countryCode: string;
  locale: string;
};

export interface RouteCffData {
  cffEconomy?: boolean;
  cffPremiumEconomy?: boolean;
  cffBusiness?: boolean;
  cffMixed?: boolean;
  hasYouth?: boolean;
  hasSenior?: boolean;
  fppEnabled?: boolean;
}

export interface LatLong {
  lat: string;
  long: string;
}

export interface Price {
  amount: string;
  currencyCode: string;
}

export interface Prices {
  [dateId: string]: Price;
}

export interface MonthPrice {
  price?: Price;
  date?: string;
  noFlight: boolean;
  isCheapest?: boolean;
  isSeparator?: boolean;
}

export interface MinMonthPrices {
  [dateId: string]: MonthPrice;
}

export interface HistogramData {
  months: MonthPrice[];
  minPrice: number;
  maxPrice: number;
}

export enum MonthType {
  MIN,
  MAX,
  OTHER,
}

export interface Availability {
  start: string;
  end: string;
}

export interface WeeklyFlights {
  start: string;
  end: string;
  weekdays: number[];
}

export interface PriceData {
  prices?: Prices;
  availability?: Availability[];
  weeklyFlights?: WeeklyFlights[];
  lowestPriceForMonth?: MinMonthPrices;
  minPriceForYear?: number;
  currency?: string;
  errorCode?: string;
}

export interface AirCalendarPrice {
  returnDate: string;
  departureDate: string;
  totalPrice: string;
  totalPoints?: string;
  currency: string;
  originalPrice?: string;
  pricePerAdult?: string;
}

export interface AirCalendarData {
  prices: AirCalendarPrice[];
}

export interface Properties extends h.JSX.HTMLAttributes<HTMLDivElement & HTMLButtonElement> {}

export interface TravelDates {
  departureDate?: string;
  returnDate?: string;
  flexible?: boolean;
}

export interface FinnairDeeplinkInterface {
  flights: {
    origin: string; // 3 digit IATA code e.g. HEL
    destination: string; // 3 digit IATA code e.g. STO
    departureDate: string; // YYYY-MM-DD format e.g. 2022-05-15
    fareFamily?: string; // e.g. BCLASIC01. Default is cheapest fare family option.
    flightNumbers?: string[]; // AY + 3/4 digits. Add zeros if the flight number is shorter. AY15 becomes AY015.
  }[];
  cabin?: string;
  directFlights?: boolean; // Whether to filter for only direct flights.
  adults?: number; // 1-9 When used, youths, seniors or b15s cannot be used.
  b15s?: number; // 0-9 Cannot be used with adults or seniors. youths + b15s must not exceed total of 9.
  c15s?: number; // 0-8; adults, C15s and children must not exceed total of 9.
  children?: number; // 0-8; adults, C15s and children must not exceed total of 9.
  infants?: number; // 0-9; infant must not exceed number of adults, youth and b15s.
  isAward?: boolean; // Internal use only! Whether to search for award flights (only available for logged in users)
  promoCode?: string; // A promo code to search with
}

export enum Key {
  ARROW_DOWN = 'ArrowDown',
  ARROW_UP = 'ArrowUp',
  ARROW_LEFT = 'ArrowLeft',
  ARROW_RIGHT = 'ArrowRight',
  ENTER = 'Enter',
  ESC = 'Escape',
  SPACE = 'Space',
  TAB = 'Tab',
}

export interface Day {
  id: string;
  value: string;
  fill?: boolean;
}

export interface Month {
  id: string;
  days: Day[];
}
export interface PreviousSearch {
  origin: {
    locationCode: string;
    cityName: string;
    type: 'city' | 'airport';
  };
  destination: {
    locationCode: string;
    cityName: string;
    type: 'city' | 'airport';
  };
  paxAmount: PaxAmount;
  travelClass: TravelClass;
  travelDates: TravelDates;
  travelType: TravelType;
  directFlights: boolean;
  creationDate: number;
  promoCode?: string;
  locale: string;
}
